import {DailyWeek} from '../types/ManagementOverviewReport.types';

export const MANAGEMENT_OVERVIEW_PAGE_HEIGHT_MM = 297;
export const MANAGEMENT_OVERVIEW_PAGE_WIDTH_MM = 210;
export const MANAGEMENT_OVERVIEW_PAGE_MARGIN_PT = 20;
export const MANAGEMENT_OVERVIEW_PAGE_USABLE_WIDTH_PT = 555;
export const MANAGEMENT_OVERVIEW_HEADER_FULL_HEIGHT_PT = 56;
export const MANAGEMENT_OVERVIEW_HEADER_COMPACT_HEIGHT_PT = 19;
export const MANAGEMENT_OVERVIEW_FOOTER_HEIGHT_PT = 15;
export const MANAGEMENT_OVERVIEW_COLUMN_MAIN_WIDTH_PT = 367.352;
export const MANAGEMENT_OVERVIEW_COLUMN_GAP_PT = 4.964;
export const MANAGEMENT_OVERVIEW_COLUMN_SIDE_WIDTH_PT = 182.683;

/** Approximate height consumed by PR charts plus section titles on page two (pt). */
export const MANAGEMENT_OVERVIEW_PAGE_TWO_CHARTS_HEIGHT_PT = 300;
/** One week header + five metric rows, including borders and cell padding. */
export const MANAGEMENT_OVERVIEW_DAILY_WEEK_BLOCK_HEIGHT_PT = 96;
export const MANAGEMENT_OVERVIEW_PAGE_BODY_HEIGHT_PT =
    MANAGEMENT_OVERVIEW_PAGE_HEIGHT_MM * 2.83465 -
    MANAGEMENT_OVERVIEW_HEADER_COMPACT_HEIGHT_PT -
    MANAGEMENT_OVERVIEW_FOOTER_HEIGHT_PT -
    MANAGEMENT_OVERVIEW_PAGE_MARGIN_PT * 2;

export interface DailyWeekPartition {
    weekName: string;
    rows: DailyWeek['rows'];
    days: DailyWeek['days'];
}

export interface DailyWeekContinuationPage {
    stationName: string;
    blocks: DailyWeekPartition[];
}

export interface PageHeightMetrics {
    clientHeight: number;
    scrollHeight: number;
}

export function pageHasVerticalOverflow(
    page: PageHeightMetrics,
    roundingTolerancePx = 2
): boolean {
    if (page.clientHeight <= 0) {
        return false;
    }
    return page.scrollHeight > page.clientHeight + roundingTolerancePx;
}

export function createInitialDailyWeekPages(dailyWeeks: DailyWeek[]): DailyWeek[][] {
    return [[...dailyWeeks]];
}

export function moveLastDailyWeekToNextPage(
    pages: DailyWeek[][],
    overflowingPageIndex: number
): DailyWeek[][] {
    const overflowingPage = pages[overflowingPageIndex];
    if (!overflowingPage || overflowingPage.length === 0) {
        return pages;
    }

    const movedWeek = overflowingPage[overflowingPage.length - 1];
    const nextPages = pages.map((page) => [...page]);
    nextPages[overflowingPageIndex] = overflowingPage.slice(0, -1);
    nextPages[overflowingPageIndex + 1] = [
        movedWeek,
        ...(pages[overflowingPageIndex + 1] ?? []),
    ];
    return nextPages;
}

export function maxDailyWeekBlocksForPageBody(
    pageBodyHeightPt: number,
    reservedHeightPt = 0
): number {
    const available = Math.max(0, pageBodyHeightPt - reservedHeightPt);
    return Math.max(1, Math.floor(available / MANAGEMENT_OVERVIEW_DAILY_WEEK_BLOCK_HEIGHT_PT));
}

export function partitionDailyWeeksForPrint(
    dailyWeeks: DailyWeek[],
    maxBlocksPerPage = 6
): DailyWeekPartition[][] {
    if (dailyWeeks.length === 0) {
        return [];
    }

    const pages: DailyWeekPartition[][] = [];
    let currentPage: DailyWeekPartition[] = [];

    dailyWeeks.forEach((week) => {
        const block: DailyWeekPartition = {
            weekName: week.weekName,
            rows: week.rows,
            days: week.days,
        };

        if (currentPage.length >= maxBlocksPerPage) {
            pages.push(currentPage);
            currentPage = [block];
            return;
        }

        currentPage.push(block);
    });

    if (currentPage.length > 0) {
        pages.push(currentPage);
    }

    return pages;
}

/**
 * Splits daily weeks across physical pages using usable body height (pt).
 * First page reserves space for monthly PR charts when {@code includeChartsOnFirstDailyPage} is true.
 */
export function partitionDailyWeeksByPageCapacity(
    dailyWeeks: DailyWeek[],
    includeChartsOnFirstDailyPage = true
): DailyWeekPartition[][] {
    if (dailyWeeks.length === 0) {
        return [];
    }

    const firstPageMax = maxDailyWeekBlocksForPageBody(
        MANAGEMENT_OVERVIEW_PAGE_BODY_HEIGHT_PT,
        includeChartsOnFirstDailyPage ? MANAGEMENT_OVERVIEW_PAGE_TWO_CHARTS_HEIGHT_PT : 0
    );
    const continuationMax = maxDailyWeekBlocksForPageBody(MANAGEMENT_OVERVIEW_PAGE_BODY_HEIGHT_PT);

    const pages: DailyWeekPartition[][] = [];
    let index = 0;
    let maxForCurrent = firstPageMax;

    while (index < dailyWeeks.length) {
        const slice = dailyWeeks.slice(index, index + maxForCurrent);
        pages.push(
            slice.map((week) => ({
                weekName: week.weekName,
                rows: week.rows,
                days: week.days,
            }))
        );
        index += slice.length;
        maxForCurrent = continuationMax;
    }

    return pages;
}

export function needsDailyWeekContinuation(dailyWeeks: DailyWeek[], maxBlocksPerPage = 6): boolean {
    return partitionDailyWeeksForPrint(dailyWeeks, maxBlocksPerPage).length > 1;
}

export function sortStationsByName<T extends { stationName: string }>(stations: T[]): T[] {
    return [...stations].sort((left, right) =>
        left.stationName.localeCompare(right.stationName, 'pt-BR', { sensitivity: 'base' })
    );
}
